import {Column} from "@ant-design/plots";
import {each, groupBy} from "@antv/util";
import {Box, Card, FormControl, Select, Typography} from "@material-ui/core";
import managerChartApi from "api/managerChart";
import {useAppDispatch, useAppSelector} from "app/hooks";
import {
  AttendInFarm,
  ProvinceForStatistic,
  ResponseAttendFarmByProvince,
} from "models/managerChart";
import React, {Fragment, useEffect, useState} from "react";
import {fetchProvincesForStatistic, selectProvincesForStatistic} from "../../managerChartSlice";
import useStyles from "./style";
import {useTranslation} from "react-i18next";

export default function ChartAttendFarmByProvince() {
  const classes = useStyles();
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const listProvincesForStatistic = useAppSelector(selectProvincesForStatistic);
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth() + 1);
  const [provinceId, setProvinceId] = useState<number>(0);
  const [currentProvinceName, setCurrentProvinceName] = useState<string>("");

  const [attendFarmByProvinceId, setAttendFarmByProvinceId] = useState<AttendInFarm[]>([]);

  const handleChangeProvince = (event: React.ChangeEvent<{value: unknown}>) => {
    setProvinceId(Number(event.target.value));
  };
  const handleChangeMonth = (event: React.ChangeEvent<{value: unknown}>) => {
    setCurrentMonth(Number(event.target.value));
  };
  const handleChangeYear = (event: React.ChangeEvent<{value: unknown}>) => {
    setCurrentYear(Number(event.target.value));
  };

  useEffect(() => {
    dispatch(fetchProvincesForStatistic());
  }, []);
  useEffect(() => {
    if (listProvincesForStatistic.length > 0) {
      setProvinceId(listProvincesForStatistic[0].id);
      setCurrentProvinceName(listProvincesForStatistic[0].name);
    }
  }, [listProvincesForStatistic]);

  useEffect(() => {
    if (provinceId === 0) return;
    (async () => {
      try {
        const res: ResponseAttendFarmByProvince<AttendInFarm> =
          await managerChartApi.getStatisticAttendFarmByProvince({
            month: currentMonth,
            year: currentYear,
            newProvinceId: provinceId,
          });
        if (res.responseCode === "00") {
          setAttendFarmByProvinceId(res.attendanceInFarms);
          setCurrentProvinceName(res.provinceName);
        }
      } catch (error) {}
    })();
  }, [currentYear, provinceId, currentMonth]);

  const annotations: any = [];
  each(groupBy(attendFarmByProvinceId, "farmName"), (values: any, farm: any) => {
    const total = values.reduce((a: any, b: any) => a + b.attendanceCount.totalStudents, 0);
    annotations.push({
      type: "text",
      content: `${total}`,
      position: [farm, total],
      style: {
        textAlign: "center",
        fontSize: 14,
        fill: "rgba(0,0,0,0.85)",
      },
      offsetY: -10,
    });
  });

  const config: any = (dataStatistic: AttendInFarm[], annotation: any) => ({
    data:
      dataStatistic
        .map((item) => [
          {
            farm: item.farmName,
            value: item.attendanceCount.fullyWatched,
            status: `${t("chart.hocdu")}`,
          },
          {
            farm: item.farmName,
            value: item.attendanceCount.notFullyWatched,
            status: `${t("chart.hocthieu")}`,
          },
          {
            farm: item.farmName,
            value: item.attendanceCount.noWatched,
            status: `${t("chart.bohoc")}`,
          },
        ])
        .reduce((previosValue, currentValue) => {
          return previosValue.concat(currentValue);
        }, []) || [],
    isStack: true,
    xField: "farm",
    yField: "value",
    seriesField: "status",

    color: ({status}: any) => {
      if (status === `${t("chart.hocdu")}`) {
        return " #123664";
      } else if (status === `${t("chart.hocthieu")}`) {
        return "#EBA337";
      }
      return "#B80F0F";
    },
    maxColumnWidth:130,
    state: {
      active: {
        animate: {duration: 100, easing: "easeLinear"},
        style: {
          lineWidth: 2,
          stroke: "#000",
        },
      },
    },
    padding: 50,
    label: {
      position: "middle",
      style: {
        fill: "#fff",
      },
      formatter: (data: any) => {
        const itemFilter = dataStatistic.filter(
          (item: AttendInFarm) => item.farmName === data.farm
        )[0];
        const total = itemFilter.attendanceCount.totalStudents;
        if (data.value === 0) {
          return;
        }

        return Number.isInteger((data.value / total) * 100)
          ? `${(data.value / total) * 100}%`
          : `${((data.value / total) * 100).toFixed(1)}%`;
      },

      layout: [
        {
          type: "interval-adjust-position",
        },
      ],
    },
    interactions: [
      {
        type: "element-highlight-by-color",
      },
      {
        type: "element-link",
      },
    ],
    annotations: annotation,
    legend: {
      position: "bottom",
      offsetY: 5,
    },
  });

  return (
    <Box>
      <Box mr={2} mb={2} textAlign="right">
        <FormControl variant="outlined" className={classes.formControl} style={{minWidth: 230}}>
          <Select
            native
            value={provinceId}
            onChange={handleChangeProvince}
            defaultValue={provinceId}
          >
            {listProvincesForStatistic?.map((provinces: ProvinceForStatistic) => (
              <option value={provinces.id} key={provinces.id}>
                {provinces.name}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" className={classes.formControl}>
          <Select
            native
            value={currentMonth}
            onChange={handleChangeMonth}
            defaultValue={currentMonth}
          >
            {Array.from(new Array(12)).map((id, index) => (
              <Fragment key={index.toString()}>
                {index === 0 && <option value="">{t("tatca")}</option>}
                <option value={index + 1}>
                  {t("chart.thang")} {index + 1}
                </option>
              </Fragment>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" className={classes.formControl}>
          <Select native value={currentYear} onChange={handleChangeYear} defaultValue={currentYear}>
            {Array.from(new Array(10)).map((id, index) => (
              <option value={index + 2020} key={index.toString()}>
                {index + 2020}
              </option>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <Box className={classes.title__province}>
          <Typography variant="subtitle2" style={{color: "#DC2138"}}>
            {currentProvinceName}
          </Typography>
          <hr
            style={{
              margin: "0 20px",
              flexGrow: 1,
            }}
          />
        </Box>
        {attendFarmByProvinceId.filter(
          (item: AttendInFarm) => item.attendanceCount.totalStudents !== 0
        ).length > 0 ? (
          <Card style={{padding: 20}}>
            <Column {...config(attendFarmByProvinceId, annotations)} />
          </Card>
        ) : (
          <Typography variant="subtitle2">{t("chart.khongcodulieu")}</Typography>
        )}
      </Box>
    </Box>
  );
}
